import React, { useRef, useState } from "react";
import styled from "styled-components";
import { IPreviewPlayerCard } from "../../types/IPlayerCardDetail";
import { useNavigate } from "react-router-dom";
import LabelNumber from "../../styled-components/LabelNumber";
import noImageplayer from "../../assets/icons/noImagePlayer.svg";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { InfoOutlined } from "@mui/icons-material";
import GoldStar from "../../assets/icons/GoldStar.png";
import { ArchetypeInfoDrawer } from "../Chemistry/UIContent";
import InfoIcon from "../../assets/icons/infoicon.svg";
import LabelText from "../../ui/LabelText";
import ChipComponent from "../../ui/Chip";

const PreviewPlayerCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #dadada;
  background: ${({ theme }) => theme.playerContentBackground};
  width: 100%;
  max-width: 320px;
  min-width: 300px;

  @media print {
    width: 100% !important;
    max-width: 320px !important;
  }
`;

const PlayerHeader = styled.div`
  display: flex;
  flex-shrink: 0;
  align-self: stretch;
  padding: 0px 34px;
  justify-content: center;
  margin-bottom: 12px;
  position: relative;
`;

const PlayerAvatar = styled.div<{ backgroundImage: string }>`
  width: 180px;
  height: 180px;
  flex-shrink: 0;
  border-radius: 328.125px;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.borderColor};
  background: url(${(props) => props.backgroundImage}) lightgray 50% / cover no-repeat;
`;

const PlayerNameHeader = styled.div`
  display: contents;
  cursor: pointer;
  .squad-number {
    justify-content: space-between;
    display: flex;
    align-items: center;
    align-self: stretch;
  }
  .position-text {
    cursor: default;
    display: flex;
    justify-content: flex-start;
  }
  & .drawer-title {
    height: 100%;
    font-family: "Aeonik";
    font-weight: 700;
    font-size: 28px;
    line-height: 36px;
  }
`;

const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: 100%;
  justify-content: space-between;
`;

const PlayerCardFooter = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 15px;
`;

const getBackgroundColor = (value?: number): string => {
  if (value == null) {
    return "none";
  }
  value = Math.ceil(value);
  if (value > 0 && value <= 24.99) {
    return "var(--UI-red-500, #FF3864)";
  } else if (value >= 25 && value <= 49.99) {
    return "var(--Brand-orange-500, #FF7800)";
  } else if (value >= 50 && value <= 74.99) {
    return "var(--UI-yellow-500, #F2E94E)";
  } else if (value >= 75 && value <= 87.99) {
    return "#C9F0B6";
  } else if (value >= 88 && value <= 98.99) {
    return "var(--Brand-green-2-500, #4AF075)";
  } else if (value >= 99) {
    return "#00D1A8";
  } else if (value === 0) {
    return "#DADADA";
  } else {
    return "red";
  }
};

const PreviewPlayerCardComponent: React.FC<IPreviewPlayerCard> = ({
  playerId,
  givenName,
  familyname,
  playerImageURL,
  isInjured,
  onLoan,
  givenOnLoan,
  primaryPlayingPosition,
  age,
  heightInCM,
  playerAttributesByPosition,
  contributionValue,
  performanceValue,
  clubImageURL,
  archetypeName,
  archetypeId,
  trueValueText,
  wagesPWText,
  playerAgent,
  clubName,
  nationalityCountry,
  nationalityCountryCode,
}) => {
  const navigate = useNavigate();
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    tooltipTimeout.current = setTimeout(() => {
      setShowTooltip(true);
    }, 500);
  };

  const handleMouseLeave = () => {
    if (tooltipTimeout.current) {
      clearTimeout(tooltipTimeout.current);
    }
    setShowTooltip(false);
  };

  const [infoHovered, setInfoHovered] = useState<boolean>(false);
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnterContribution = () => {
    hoverTimeout.current = setTimeout(() => {
      setInfoHovered(true);
    }, 500);
  };

  const handleMouseLeaveContribution = () => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
    }
    setInfoHovered(false);
  };

  const goToDetailPage = () => {
    navigate(`/player/${playerId}`);
  };

  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);

  return (
    <PreviewPlayerCard>
        <PlayerHeader>
          <div className="status-avatar-container">
            <div className="status-label">
              {onLoan && <ChipComponent status={"LOANED IN"} />}
              {givenOnLoan && <ChipComponent status={"LOANED OUT"} />}
              {isInjured && <ChipComponent status={"INJURED"} />}
            </div>
            <PlayerAvatar onClick={() => goToDetailPage()} backgroundImage={playerImageURL ? playerImageURL : noImageplayer} />
          </div>
        </PlayerHeader>
        <CardInfo>
          <PlayerNameHeader>
            <div className="squad-number">
              <LabelText className="player-given-name" onClick={() => goToDetailPage()} style={{ fontSize: '16px' }}>
                {givenName}
              </LabelText>
            </div>
            <LabelText className="drawer-title" onClick={() => goToDetailPage()}>
              {familyname}
            </LabelText>
            {(clubName || nationalityCountry) && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '4px',
                flexWrap: 'wrap'
              }}>
                {clubName && (
                  <LabelNumber className="position-text" style={{ fontSize: '16px', margin: 0, display: 'flex', alignItems: 'center', color: '#746d6dff' }}>
                    {clubImageURL && (
                      <img
                        src={clubImageURL}
                        alt="Club"
                        style={{
                          width: '18px',
                          height: '18px',
                          marginRight: '4px',
                          borderRadius: '50%'
                        }}
                      />
                    )}
                    {clubName}
                  </LabelNumber>
                )}
                {clubName && nationalityCountry && (
                  <span style={{ color: '#666', fontSize: '12px' }}>•</span>
                )}
                {nationalityCountry && nationalityCountryCode && (
                  <LabelNumber className="position-text" style={{ fontSize: '16px', margin: 0, display: 'flex', alignItems: 'center', color: '#746d6dff' }}>
                    <img
                      src={`/assets/country_flags/${nationalityCountryCode}.svg`}
                      alt="Country Flag"
                      style={{
                        width: '18px',
                        height: '18px',
                        marginRight: '4px',
                        borderRadius: '50%'
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    {nationalityCountry}
                  </LabelNumber>
                )}
              </div>
            )}
            <LabelNumber className="position-text" style={{ fontSize: '13px' }}>Position : {primaryPlayingPosition}</LabelNumber>
            {trueValueText && <LabelNumber className="position-text" style={{ fontSize: '13px' }}>True Value : {trueValueText}</LabelNumber>}
            {wagesPWText && <LabelNumber className="position-text" style={{ fontSize: '13px' }}>Wages : {wagesPWText}</LabelNumber>}
            {playerAgent && <LabelNumber className="position-text" style={{ fontSize: '13px' }}>Agent : {playerAgent}</LabelNumber>}
            {archetypeName && (
              <LabelNumber className="position-text" style={{ fontSize: '13px' }}>
                {archetypeName}
                <img src={InfoIcon} alt="archetype-info" className="archetype-info-icon-sortlist" onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  document.querySelectorAll('.squad-pitch-player-card').forEach((card) => {
                    (card as HTMLElement).style.zIndex = '9999';
                  });
                  setSidebarOpen(true);
                }} />
              </LabelNumber>
            )}
          </PlayerNameHeader>
          <PlayerCardFooter>
            <div className="age-height-container">
              <p>
                AGE <span className="age-height-value">{age ? age : "--"}</span>
              </p>
              <span className="age-height-dot">.</span>
              <p>
                HEIGHT <span className="age-height-value">{heightInCM ? heightInCM : "--"}CM</span>
              </p>
            </div>
            {playerAttributesByPosition?.length === 0 && (
              <div className="gk-attribute-container">
                {primaryPlayingPosition === "Goalkeeper" ? (
                  <div>
                    <span>Goalkeeper attributes</span>
                    <span>not currently available</span>
                  </div>
                ) : (
                  <div>
                    <span>Player attributes</span>
                    <span>not currently available</span>
                  </div>
                )}
              </div>
            )}
            {playerAttributesByPosition?.length > 0 && (
              <div className="squad-atrribute-container">
                {playerAttributesByPosition?.map((attribute) => (
                  <div
                    className="squad-player-attributes"
                    style={{
                      backgroundColor: getBackgroundColor(attribute?.attributeValue),
                      borderColor: getBackgroundColor(attribute?.attributeValue),
                      color: attribute?.attributeValue <= 49.99 && attribute?.attributeValue >= 1 ? "#FFFFFF" : "#111111",
                    }}
                  >
                    <p>{attribute?.attributeName}</p>
                    <div className="attribute-value-container">
                      {attribute?.attributeValue === 0 && showTooltip && <div>Not enough data</div>}
                      {attribute?.attributeValue === 0 && <InfoOutlined onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} />}
                      <p>
                        {attribute?.attributeValue >= 99 ? (
                          <span className="star-performer-container">
                            <img src={GoldStar} alt="star-performer" />
                          </span>
                        ) : null}{" "}
                        {attribute?.attributeValue >= 1 ? attribute?.attributeValue === 100 ? "99.99" : attribute?.attributeValue?.toFixed(2) : "--"}
                      </p>
                    </div>
                  </div>
                ))}
                <div className="squad-contribution-attributes">
                  <div className="contribution-icon">
                    <p>ATTACK CONTRIBUTION</p>
                    <InfoOutlinedIcon onMouseEnter={handleMouseEnterContribution} onMouseLeave={handleMouseLeaveContribution} />
                    {infoHovered && <div>Attack contribution by position</div>}
                  </div>
                  <p>{contributionValue ? contributionValue : performanceValue ? performanceValue : "--"}</p>
                </div>
              </div>
            )}
          </PlayerCardFooter>
        </CardInfo>
        {archetypeName && archetypeId && <ArchetypeInfoDrawer
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          archetypeName={archetypeName!}
          activeArchetypeID={archetypeId!}
          playerPosition={primaryPlayingPosition}
        />}
      </PreviewPlayerCard>
  );
};

export default PreviewPlayerCardComponent;